import { useCallback, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AgGridReact } from 'ag-grid-react';
import type { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useCreateNewSireMutation } from '@/app/services/donorSchedules';
import { addToast } from '@/app/slices/toast';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { SireSaveWarningDialog } from '@/components/common/sire-save-warning-dialog';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useRevisionIncrement } from '@/hooks/useRevisionIncrement';
import { cn } from '@/lib/utils';
import { queryClient } from '@/services';
import { useAccessToken } from '@/services/auth';
import {
  sireDuplicateCheckOptions,
  useCreateSireMutation,
} from '@/services/sire';
import { verifiedOptions } from '@/types/common';
import { appendRevisionComment } from '@/utils/formatting';

import { BreedsAutoComplete } from '../breeds-auto-complete/BreedsAutoComplete';
import { BreedsCombobox } from '../breeds-combobox';
import { sireFormSchema, SireFormValues } from '../new-sire-dialog/schema';
import { SpeciesDropdown } from '../species-dropdown/SpeciesDropdown';

type NewSireDialogProps = {
  visible: boolean;
  onHide: () => void;
  initialValues?: Partial<SireFormValues>;
  gridRef?: React.RefObject<AgGridReact<any>>;
  revisionComment: string;
  setRevisionComment: (revisionComment: string) => void;
  revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber: (revisionNumber: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
};

const toOptionalNumber = (value: string) =>
  value === '' ? undefined : Number(value);

export const NewSireDialog = ({
  visible,
  onHide,
  initialValues,
  gridRef,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
}: NewSireDialogProps) => {
  const { t } = useTranslation();
  const token = useAccessToken();
  const dispatch = useDispatch();
  const [createSire] = useCreateNewSireMutation();
  const { mutateAsync: createSireRest } = useCreateSireMutation();
  const isSireVerifiedEnabled = useFeatureFlag('Sire_Verified_Field');
  const isDuplicateSireCheckEnabled = useFeatureFlag('Add_Duplicate_Sire_Flag');
  const isSaveWarningEnabled = useFeatureFlag('Sire_Save_Warning');
  const isBreedComboboxEnabled = useFeatureFlag(
    'Account_Combobox_not_Dropdown',
  );
  const [pendingSave, setPendingSave] = useState<SireFormValues | null>(null);
  const [params] = useSearchParams();
  const isRevisionMode = params.get('revision') === 'true';
  const { incrementRevisionWithTopRevisions } = useRevisionIncrement();
  const [selectedSubFamilies, setSelectedSubFamilies] = useState<number[]>([]);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement | null>(
    null,
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<SireFormValues>({
    resolver: zodResolver(sireFormSchema),
    defaultValues: {
      name: undefined,
      breedId: undefined,
      sire: undefined,
      regPrefix: undefined,
      registrationNumber: undefined,
      sireTpi: undefined,
      sireCm: undefined,
      sireJpi: undefined,
      sireNm: undefined,
      cane: undefined,
      sexedCane: undefined,
      birth: undefined,
      heparin: 30,
      breedReg: undefined,
      verified: 'No',
      ...initialValues,
    },
  });

  const getFormErrorMessage = (name: keyof SireFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const showSaveWarning = (message: string | null | undefined) =>
    dispatch(
      addToast({
        severity: 'warn',
        summary: t('common.error'),
        detail: message,
        life: ToastLife.MEDIUM,
      }),
    );

  const checkDuplicateSire = useCallback(
    async (breedId: string | undefined, shortName: string | undefined) => {
      const trimmedShortName = shortName?.trim();
      if (!isDuplicateSireCheckEnabled || !breedId || !trimmedShortName) {
        return false;
      }
      const { isDuplicate } = await queryClient.fetchQuery(
        sireDuplicateCheckOptions(token, {
          breedId,
          shortName: trimmedShortName,
        }),
      );
      if (isDuplicate) {
        setError('name', {
          type: 'duplicate',
          message: t('sire.duplicateBreedShortName'),
        });
      } else if (getFieldState('name').error?.type === 'duplicate') {
        clearErrors('name');
      }
      return isDuplicate;
    },
    [
      isDuplicateSireCheckEnabled,
      setError,
      clearErrors,
      getFieldState,
      t,
      token,
    ],
  );

  const checkDuplicateSireSilently = useCallback(
    (breedId: string | undefined, shortName: string | undefined) => {
      checkDuplicateSire(breedId, shortName).catch((error) =>
        console.error('Failed to check for duplicate Sire:', error),
      );
    },
    [checkDuplicateSire],
  );

  const onSave = async ({ verified, ...input }: SireFormValues) => {
    try {
      const isDuplicate = await checkDuplicateSire(
        input.breedId,
        input.name,
      ).catch(() => false);
      if (isDuplicate) {
        return;
      }
      if (isSireVerifiedEnabled) {
        const res = await createSireRest({
          ...input,
          verified: verified === 'Yes',
        });
        if (!res.success) {
          showSaveWarning(res.message);
          return;
        }
      } else {
        const res = await createSire({ input }).unwrap();
        if (res.createNewSire.success === false) {
          showSaveWarning(res.createNewSire.message);
        }
      }
      if (params.get('revision') === 'true') {
        const newNoteText = `Input Sire, ${input.name}`;
        setRevisionComment(appendRevisionComment(revisionComment, newNoteText));
        setRevisionNumber(
          incrementRevisionWithTopRevisions(
            revisionNumber.previousRevision,
            revisionNumber.currentRevision,
          ),
        );
      }
      gridRef?.current?.api.refreshServerSide();
      gridRef?.current?.api.redrawRows();
      reset();
      onHide();
    } catch (error) {
      console.error('Failed to create Sire:', error);
    }
  };

  const onSaveRequested = async (values: SireFormValues) => {
    if (isSaveWarningEnabled) {
      setPendingSave(values);
      return;
    }
    await onSave(values);
  };

  const saveButton = (
    <Button
      type="button"
      onClick={handleSubmit(onSaveRequested)}
      className={cn(
        isRevisionMode &&
          'border-yellow-500 bg-yellow-500 text-black hover:bg-yellow-500/90',
      )}
    >
      {t('common.save')}
    </Button>
  );

  return (
    <Dialog
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      <DialogContent
        ref={setDialogContainer}
        className="z-[2000] md:max-w-[1200px]"
        overlayClassName="z-[2000]"
        showCloseButton
      >
        <DialogHeader>
          <DialogTitle>{t('sire.newSire')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            onSubmit={handleSubmit(onSaveRequested)}
            className="grid grid-cols-3 gap-4 p-4"
          >
            <div className="label-input-group">
              <label htmlFor="name">{t('fields.shortName')}*</label>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={() => {
                      field.onBlur();
                      checkDuplicateSireSilently(
                        getValues('breedId'),
                        getValues('name'),
                      );
                    }}
                    tabIndex={1}
                  />
                )}
              />
              {getFormErrorMessage('name')}
            </div>

            <SpeciesDropdown
              label={t('fields.species', 'Species')}
              tabIndex={2}
              onChange={(subFamilies) => {
                setSelectedSubFamilies(subFamilies);
                setValue('breedId', '');
              }}
            />

            <div className="label-input-group">
              <Controller
                name="breedId"
                control={control}
                render={({ field, fieldState }) =>
                  isBreedComboboxEnabled ? (
                    <>
                      <label htmlFor="breedId">{`${t('fields.breed')}*`}</label>
                      <BreedsCombobox
                        key={selectedSubFamilies.join(',')}
                        id="breedId"
                        value={field.value || undefined}
                        subFamilies={selectedSubFamilies}
                        container={dialogContainer ?? undefined}
                        invalid={fieldState.invalid}
                        tabIndex={3}
                        onChange={(breed) => {
                          field.onChange(breed?.id ?? '');
                          checkDuplicateSireSilently(
                            breed?.id,
                            getValues('name'),
                          );
                        }}
                      />
                    </>
                  ) : (
                    <BreedsAutoComplete
                      key={selectedSubFamilies.join(',')}
                      label={`${t('fields.breed')}*`}
                      className={cn(
                        'w-full',
                        fieldState.invalid && 'p-invalid',
                      )}
                      subFamilies={selectedSubFamilies}
                      tabIndex={3}
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        field.onChange(e.value?.id);
                        checkDuplicateSireSilently(
                          e.value?.id,
                          getValues('name'),
                        );
                      }}
                    />
                  )
                }
              />
              {getFormErrorMessage('breedId')}
            </div>

            <div className="label-input-group">
              <label htmlFor="sire">{t('sire.pedigreeName')}*</label>
              <Controller
                name="sire"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={4}
                  />
                )}
              />
              {getFormErrorMessage('sire')}
            </div>

            <div className="label-input-group">
              <label htmlFor="regPrefix">{t('fields.countryCode')}</label>
              <Controller
                name="regPrefix"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={5}
                  />
                )}
              />
              {getFormErrorMessage('regPrefix')}
            </div>

            <div className="label-input-group">
              <label htmlFor="registrationNumber">
                {t('fields.registrationNumber')}
              </label>
              <Controller
                name="registrationNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    aria-invalid={fieldState.invalid}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={6}
                  />
                )}
              />
              {getFormErrorMessage('registrationNumber')}
            </div>

            <div className="label-input-group">
              <label htmlFor="sireTpi">{t('sire.tpi')}</label>
              <Controller
                name="sireTpi"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(toOptionalNumber(e.target.value))
                    }
                    tabIndex={7}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="sireCm">{t('sire.cmDollar')}</label>
              <Controller
                name="sireCm"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(toOptionalNumber(e.target.value))
                    }
                    tabIndex={8}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="sireJpi">{t('sire.jpi')}</label>
              <Controller
                name="sireJpi"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(toOptionalNumber(e.target.value))
                    }
                    tabIndex={9}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="sireNm">{t('sire.nm')}</label>
              <Controller
                name="sireNm"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(toOptionalNumber(e.target.value))
                    }
                    tabIndex={10}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="cane">{t('sire.caneNaab')}</label>
              <Controller
                name="cane"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={11}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="sexedCane">{t('sire.sexedCaneCode')}</label>
              <Controller
                name="sexedCane"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={12}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="birth">{t('fields.birthDate')}</label>
              <Controller
                name="birth"
                control={control}
                render={({ field }) => (
                  <Calendar
                    id={field.name}
                    value={field.value}
                    className="w-full"
                    onChange={(e) => field.onChange(e.value)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    showOnFocus={false}
                    tabIndex={13}
                    container={dialogContainer ?? undefined}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="heparin">{t('sire.heparinConcentration')}</label>
              <Controller
                name="heparin"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(toOptionalNumber(e.target.value))
                    }
                    tabIndex={14}
                  />
                )}
              />
            </div>

            <div className="label-input-group">
              <label htmlFor="breedReg">
                {t('sire.breedRegistrationCane')}
              </label>
              <Controller
                name="breedReg"
                control={control}
                render={({ field }) => (
                  <Input
                    id={field.name}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    tabIndex={15}
                  />
                )}
              />
            </div>

            {isSireVerifiedEnabled && (
              <div className="label-input-group">
                <label htmlFor="verified">{t('sire.verified')}</label>
                <Controller
                  name="verified"
                  control={control}
                  render={({ field }) => (
                    <Select
                      items={verifiedOptions}
                      value={field.value}
                      onChange={(value) => {
                        if (value) field.onChange(value);
                      }}
                    >
                      <SelectTrigger
                        id={field.name}
                        data-testid="verified"
                        className="w-full"
                        tabIndex={16}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {verifiedOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              reset();
              onHide();
            }}
          >
            {t('common.cancel')}
          </Button>
          {isRevisionMode ? (
            <Tooltip>
              <TooltipTrigger render={saveButton} />
              <TooltipContent>
                {t('revision.revisionModeFieldTooltip')}
              </TooltipContent>
            </Tooltip>
          ) : (
            saveButton
          )}
        </DialogFooter>
        <SireSaveWarningDialog
          open={pendingSave !== null}
          mode="new"
          onCancel={() => setPendingSave(null)}
          onConfirm={() => {
            const values = pendingSave;
            setPendingSave(null);
            if (values) {
              void onSave(values);
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
